import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Form } from "react-bootstrap";
import axios from "axios";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import Header from "./../components/Header";
import LeftNav from "./../components/LeftNav";
import AskAthena from "../components/AskAthena";
import UserService from "../services/UserService";
import RightAside from "./../components/RightAside";

const serverConfig = require("../config/server.js");

// dashboard_list_get returns one row per (product, real subscriber) -- admin/
// unfiltered views need to collapse that back to one row per product,
// preferring a deployed subscriber's row if any exists.
function dedupeByProduct(items) {
  const map = new Map();
  for (const item of items) {
    const existing = map.get(item.product_id);
    if (!existing || (item.is_deployed === 1 && existing.is_deployed !== 1)) {
      map.set(item.product_id, item);
    }
  }
  return Array.from(map.values());
}

const Subscription = () => {
  const navigate = useNavigate();

  const [token, setToken] = useState(null);
  const [clientLogo, setClientLogo] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [viewReportsPrivileged, setViewReportsPrivileged] = useState(false);

  const [pinnedReport, setPinnedReport] = useState(false);
  const [iframeUrl, setIFrameUrl] = useState(null);
  const [iframeName, setIFrameName] = useState(null);
  const [dashboardListSubscribed, setDashboardListSubscribed] = useState([]);
  // Raw, un-scoped, client-wide subscribed rows (every real subscriber, not
  // just the viewer) -- used ONLY by the admin "Filter by User" control.
  // dashboardListSubscribed itself is always the viewer's OWN subscriptions.
  const [allClientSubscribedItems, setAllClientSubscribedItems] = useState([]);
  const [dashboardListSubscribedFinal, setDashboardListSubscribedFinal] =
    useState([]);
  const [dashboardListUnsubscribed, setDashboardListUnsubscribed] = useState(
    []
  );
  const [dashboardListUnsubscribedFinal, setDashboardListUnsubscribedFinal] =
    useState([]);
  const [subscribedFunctionList, setSubscribedFunctionList] = useState([]);
  const [unsubscribedFunctionList, setUnsubscribedFunctionList] = useState([]);
  const [selectFunctions, setSelectFunctions] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [refreshNotifications, setRefreshNotifications] = useState(false);
  const [userList, setUserList] = useState([]);
  const [selectedFunction, setSelectedFunction] = useState(null);

  const handleLogout = () => {
    sessionStorage.clear();
    UserService.doLogout();
  };

  const getToken = () => {
    console.log(sessionStorage.getItem("token"));
    let jwt = UserService.getToken();
    axios
      .post(serverConfig.api_base_url + "user_get_token", { jwt: jwt })
      .then((response) => {
        console.log("user_get_token response", response);
        if (response.data.status === 200) {
          sessionStorage.setItem("token", response.data.token);
          setToken(response.data.token);
        } else if (response.data.status >= 400 && response.data.status <= 499) {
          alert(response.data.message);
        } else if (response.data.jwt_validation === "fail") {
          handleLogout();
        }
      })
      .catch((err) => console.log("error is", err));
  };

  useEffect(() => {
    console.log(sessionStorage.getItem("token"));
    if (sessionStorage.getItem("token") === null) {
      getToken();
    } else {
      setToken(sessionStorage.getItem("token"));
    }
  }, []);

  useEffect(() => {
    if (token !== null && token !== undefined) {
      axios
        .post(serverConfig.api_base_url + "user_check_privilege", {
          token: token,
          privilege: "view_reports",
        })
        .then((response) => {
          console.log("user_check_privilege response", response.data);
          if (response.data.status === 200) {
            if (response.data.is_privileged === 1) {
              setViewReportsPrivileged(true);
            } else if (response.data.is_privileged === 0) {
              setViewReportsPrivileged(false);
            }
          } else if (response.data.status === 301) {
            handleLogout();
          } else {
            alert(response.data.message);
          }
        })
        .catch((err) => console.log("error is", err));

      axios
        .post(serverConfig.api_base_url + "modules_subscribed_get", {
          token: token,
        })
        .then((response) => {
          console.log("modules_subscribed_get response", response.data.items);
          if (response.data.status === 200) {
            if (response.data.items !== null) {
              setFunctions(response.data.items);
            }
          } else if (response.data.status === 301) {
            handleLogout();
          } else {
            alert(response.data.message);
          }
        })
        .catch((err) => console.log("error is", err));

      axios
        .post(serverConfig.api_base_url + "lookup_list", {
          name: "select_functions",
          token: token,
        })
        .then((response) => {
          console.log("lookup_list_response response", response.data.items);
          if (response.data.status === 200) {
            if (response.data.items !== null) {
              response.data.items.sort(function (a, b) {
                return a.seq_no - b.seq_no;
              });
              setSelectFunctions(response.data.items);
            }
          } else if (response.data.status === 301) {
            handleLogout();
          } else {
            alert(response.data.message);
          }
        })
        .catch((err) => console.log("error is", err));
    }
  }, [token]);

  // Rebuilds the base subscribed list, function chips, and default
  // function+display for whichever email is currently being viewed -- the
  // viewer's own by default, or a specifically-selected other person via
  // "Filter by User". Shared by the initial load and handleFilterByUser so
  // both stay in sync: switching users updates the available function chips
  // too, not just the product list.
  const applySubscribedUserView = (rawItems, email) => {
    const userItems = rawItems.filter((el) => el.email_id === email);
    setDashboardListSubscribed(userItems);

    setSubscribedFunctionList([
      ...new Set(userItems.map((item) => item.function_name)),
    ]);

    const firstFunction = [...userItems]
      .sort((a, b) => a.seq_no - b.seq_no)
      .map((item) => item.function_name)[0];
    const pinnedItem = userItems.find((el) => el.pinned === 1);
    const defaultFunction = pinnedItem ? pinnedItem.function_name : firstFunction;

    setSelectedFunction(defaultFunction);
    setDashboardListSubscribedFinal(
      userItems.filter((el) => el.function_name === defaultFunction)
    );
  };

  useEffect(() => {
    if (token !== null && token !== undefined && selectFunctions.length > 0) {
      axios
        .post(serverConfig.api_base_url + "dashboard_list_get", {
          token: token,
        })
        .then((response) => {
          console.log("dashboard_list_get response", response);
          if (response.data.status === 200) {
            if (response.data.items !== null) {
              // dashboard_list_get returns one row per (product, real subscriber).
              // The default view -- page load, and clicking a Filter by Function
              // chip -- is ALWAYS the viewer's own subscriptions, admin or not.
              // Only the admin-only "Filter by User" control explicitly shows a
              // different, specifically-selected person's rows (via
              // allClientSubscribedItems, the raw/un-scoped list kept alongside).
              const myEmail = UserService.getUsername();
              const rawSubscribed = response.data.items.filter(
                (el) => el.is_subscribed === 1
              );
              setAllClientSubscribedItems(rawSubscribed);
              setUserList([
                ...new Set(rawSubscribed.map((item) => item.email_id)),
              ]);

              const subscribedItems = rawSubscribed.filter(
                (el) => el.email_id === myEmail
              );
              applySubscribedUserView(rawSubscribed, myEmail);

              // "Subscribe More" -- everything client-enabled that the viewer
              // personally has no subscription row for.
              const unsubscribedItems = dedupeByProduct(
                response.data.items
              ).filter(
                (el) =>
                  !subscribedItems.some(
                    (sub) => sub.product_id === el.product_id
                  )
              );
              setDashboardListUnsubscribed(unsubscribedItems);
              setUnsubscribedFunctionList([
                ...new Set(unsubscribedItems.map((item) => item.function_name)),
              ]);

              // Load the pinned product in the iframe if one exists, otherwise
              // fall back to the first deployed product -- the viewer's own.
              const pinnedDeployed = subscribedItems.find(
                (el) => el.pinned === 1 && el.is_deployed === 1
              );
              if (pinnedDeployed) {
                setIFrameUrl(pinnedDeployed.link);
              } else {
                const firstDeployed = subscribedItems.find(
                  (el) => el.is_deployed === 1
                );
                if (firstDeployed) setIFrameUrl(firstDeployed.link);
              }
              setIsLoading(false);
            } else {
              setDashboardListSubscribed([]);
              setDashboardListSubscribedFinal([]);
              setDashboardListUnsubscribed([]);
              setDashboardListUnsubscribedFinal([]);
              setIsLoading(false);
            }
          } else if (response.data.status === 301) {
            handleLogout();
          } else {
            alert(response.data.message);
            setIsLoading(false);
          }
        })
        .catch((err) => console.log("error is", err));
    }
  }, [token, selectFunctions, pinnedReport]);

  const handleReportPin = (productid, toPin) => {
    axios
      .post(serverConfig.api_base_url + "report_pin", {
        token: token,
        product_id: productid,
        to_pin: toPin,
      })
      .then((response) => {
        setIsLoading(true);
        console.log("report_pin response:", response);
        if (response.data.status === 200) {
          console.log("success");
          setPinnedReport(!pinnedReport);
        } else if (response.data.status === 301) {
          handleLogout();
        } else {
          alert(response.data.message);
          setIsLoading(false);
        }
      })
      .catch((err) => console.log("error is", err));
  };

  const handleRequest = (itemName, event) => {
    event.stopPropagation();
    console.log(itemName);
    console.log(event.target.innerText);
    var message;
    if (event.target.innerText === "Subscribe") {
      message = `Subscription requested for ${itemName}`;
    } else if (event.target.innerText === "Start Trial") {
      message = `Trial requested for ${itemName}`;
    } else {
      message = `Subscription requested for ${itemName}`;
    }
    axios
      .post(serverConfig.api_base_url + "notification_set", {
        token: token,
        notificationText: message,
      })
      .then((response) => {
        console.log("notification_set response:", response);
        if (response.data.status === 200) {
          alert(message);
          setRefreshNotifications(!refreshNotifications);
        } else if (response.data.status === 301) {
          handleLogout();
        } else {
          alert(response.data.message);
        }
      })
      .catch((err) => console.log("error is", err));
  };

  const handleFunctionsCheck = (event) => {
    setSelectedFunction(event.target.value);
    setDashboardListSubscribedFinal(
      dashboardListSubscribed.filter(
        (el) => el.function_name === event.target.value
      )
    );
  };

  const handleUnsubscribedFunctionsCheck = (event) => {
    console.log(event.target.value);
    console.log(dashboardListUnsubscribed);
    console.log(
      dashboardListUnsubscribed.filter(
        (el) => el.function_name === event.target.value
      )
    );
    console.log(dashboardListUnsubscribedFinal);
    setDashboardListUnsubscribedFinal(
      dashboardListUnsubscribed.filter(
        (el) => el.function_name === event.target.value
      )
    );
  };

  const handleFilterByUser = (event) => {
    const email =
      event.target.value !== "" && event.target.value !== null
        ? event.target.value
        : UserService.getUsername();
    applySubscribedUserView(allClientSubscribedItems, email);
  };

  const responsive = {
    xl: {
      breakpoint: { max: 2160, min: 1280 },
      items: 6,
    },
    lg: {
      breakpoint: { max: 1280, min: 1024 },
      items: 4,
    },
    md: {
      breakpoint: { max: 1024, min: 768 },
      items: 3,
    },
    sm: {
      breakpoint: { max: 768, min: 640 },
      items: 2,
    },
    xs: {
      breakpoint: { max: 640, min: 0 },
      items: 1,
    },
  };

  return (
    <>
      <LeftNav />
      <div className="main-wrapper">
        <Header token={token} refreshNotifications={refreshNotifications} />
        <div className="d-flex content-wrap">
          <main>
            {isLoading && (
              <div id="loading">
                <img
                  src={require("./../assets/images/icons/spinner.gif")}
                  alt="Loader"
                  width="70"
                />
              </div>
            )}
            <Container fluid>
              {/* {iframeUrl && <section className="iframe-wrapper" dangerouslySetInnerHTML={{__html: iframeUrl}} />} */}
              {iframeUrl && (
                <div className="position-relative">
                  <section
                    className="iframe-wrapper"
                    dangerouslySetInnerHTML={{ __html: iframeUrl }}
                  />
                  <a
                    href={iframeUrl
                      .split(" ")
                      .find((el) => el.startsWith("src="))
                      .slice(5)
                      .slice(0, -1)}
                    target="_blank"
                    style={{
                      position: "absolute",
                      right: "5px",
                      bottom: "5px",
                    }}
                    title="Open in new tab"
                  >
                    <img
                      src={require("./../assets/images/icons/arrow-up-right-from-square.png")}
                      width="20"
                    />
                  </a>
                </div>
              )}

              {dashboardListSubscribedFinal.length > 0 && (
                <section className="bg-dark-gray mb-0 pb-0">
                  <h6 className="mb-3">My Subscriptions</h6>
                  <Carousel responsive={responsive} className="reports-list">
                    {dashboardListSubscribedFinal.map((item, index) => {
                      let btn;
                      if (item.status === "Subscribe") {
                        btn = (
                          <button
                            onClick={(e) => handleRequest(item.product_name, e)}
                          >
                            {item.status}
                          </button>
                        );
                      } else {
                        //Active or Unavailable
                        if (viewReportsPrivileged) {
                          btn = (
                            <button
                              onClick={(e) => e.stopPropagation()}
                              className={
                                item.status === "Active"
                                  ? "txt-yellow"
                                  : "txt-gray"
                              }
                            >
                              {item.status}
                            </button>
                          );
                        } else {
                          btn = (
                            <button
                              onClick={() => navigate("/products")}
                              className={
                                item.status === "Active"
                                  ? "txt-yellow"
                                  : "txt-gray"
                              }
                            >
                              {item.status}
                            </button>
                          );
                        }
                      }
                      return (
                        /* functions.some(el => el.function === item.function_name) && */ <div
                          xs="auto"
                          key={index}
                          className={
                            item.status === "Unavailable"
                              ? "unavailable"
                              : undefined
                          }
                        >
                          <div
                            className={
                              item.pinned === 1 ? "item active" : "item"
                            }
                            onClick={() => setIFrameUrl(item.link)}
                          >
                            {item.pinned === 1 ? (
                              <img
                                src={require("./../assets/images/icons/star-icon.png")}
                                alt="Pin"
                                width="12"
                                title="Remove from favourites"
                                onClick={() =>
                                  handleReportPin(item.product_id, 0)
                                }
                              />
                            ) : (
                              <img
                                src={require("./../assets/images/icons/star-empty-icon.png")}
                                alt="Pin"
                                width="12"
                                title="Add to favourites"
                                onClick={() =>
                                  handleReportPin(item.product_id, 1)
                                }
                              />
                            )}
                            <div>
                              <img
                                src={item.icon && item.icon}
                                alt={item.value}
                                width="50"
                              />
                            </div>
                            <span>{item.product_name}</span>
                            {btn}
                          </div>
                        </div>
                      );
                    })}
                  </Carousel>
                </section>
              )}

              {subscribedFunctionList.length > 0 && (
                <section className="client-subscription mt-0">
                  {/* <h2>My Subscriptions</h2>
								<ul className="subscription">
									{selectFunctions.map((item, index) => (
										functions.some(el => el.function === item.value) && <li key={index}>
											<span className="image"><img src={item.attribute1 && item.attribute1} alt={item.value} width="38" /></span>
											<Form.Check inline label={item.value} name="functions" type="checkbox" id={item.value} value={item.value}  defaultChecked={true} onChange={handleFunctionsCheck} />
										</li>
									))}
								</ul> */}
                  {!viewReportsPrivileged && <h6>Filter by Function</h6>}
                  <Carousel responsive={responsive} className="subscription">
                    {subscribedFunctionList.map((functionName, index) => {
                      const iconMatch = selectFunctions.find(
                        (sf) => sf.value === functionName
                      );
                      return (
                        <div key={index} className="label-wrap">
                          <label htmlFor={functionName}>
                            <input
                              name="functions"
                              type="radio"
                              id={functionName}
                              value={functionName}
                              onChange={handleFunctionsCheck}
                              checked={functionName === selectedFunction}
                            />
                            <span className="image">
                              {iconMatch?.attribute1 && (
                                <img
                                  src={iconMatch.attribute1}
                                  alt={functionName}
                                  width="50"
                                />
                              )}
                            </span>
                            <span className="text">{functionName}</span>
                          </label>
                        </div>
                      );
                    })}
                  </Carousel>

                  {!viewReportsPrivileged && (
                    <div className="mt-4">
                      <h6>Filter by User</h6>
                      {userList.length > 0 && (
                        <Form.Select
                          style={{ width: "300px" }}
                          onChange={handleFilterByUser}
                        >
                          <option value="">Select User</option>
                          {userList.map(
                            (usr, isridx) =>
                              usr && <option value={usr}>{usr}</option>
                          )}
                        </Form.Select>
                      )}
                    </div>
                  )}
                </section>
              )}

              {dashboardListUnsubscribedFinal.length > 0 && (
                <section className="bg-dark-gray mb-0 pb-0">
                  <h6 className="mb-3">Subscribe More</h6>
                  <Carousel responsive={responsive} className="reports-list">
                    {dashboardListUnsubscribedFinal.map((item, index) => {
                      let btn;
                      if (
                        item.status === "Subscribe" ||
                        item.status === "Trial"
                      ) {
                        btn = (
                          <button
                            onClick={(e) => handleRequest(item.product_name, e)}
                          >
                            {item.status}
                          </button>
                        );
                      } else {
                        //Active or Unavailable
                        btn = (
                          <button
                            onClick={(e) => e.stopPropagation()}
                            className={
                              item.status === "Active"
                                ? "txt-yellow"
                                : "txt-gray"
                            }
                          >
                            {item.status}
                          </button>
                        );
                      }
                      return (
                        /* functions.some(el => el.function === item.function_name) &&  */ <div
                          xs="auto"
                          key={index}
                          className={
                            item.status === "Unavailable"
                              ? "unavailable"
                              : undefined
                          }
                        >
                          <div
                            className={
                              item.pinned === 1 ? "item active" : "item"
                            }
                            onClick={() => setIFrameUrl(item.link)}
                          >
                            {item.pinned === 1 ? (
                              <img
                                src={require("./../assets/images/icons/star-icon.png")}
                                alt="Pin"
                                width="12"
                                title="Remove from favourites"
                                onClick={() =>
                                  handleReportPin(item.product_id, 0)
                                }
                              />
                            ) : (
                              <img
                                src={require("./../assets/images/icons/star-empty-icon.png")}
                                alt="Pin"
                                width="12"
                                title="Add to favourites"
                                onClick={() =>
                                  handleReportPin(item.product_id, 1)
                                }
                              />
                            )}
                            <div>
                              <img
                                src={item.icon && item.icon}
                                alt={item.value}
                                width="50"
                              />
                            </div>
                            <span>{item.product_name}</span>
                            {btn}
                          </div>
                        </div>
                      );
                    })}
                  </Carousel>
                </section>
              )}

              {unsubscribedFunctionList.length > 0 && (
                <section className="client-subscription mt-0">
                  {/* <ul className="subscription">
									{selectFunctions.map((item, index) => (
										unsubscribedFunctionList.includes(item.value) && <li key={index}>
											<span className="image"><img src={item.attribute1 && item.attribute1} alt={item.value} width="38" /></span>
											<Form.Check inline label={item.value} name="functions" type="checkbox" id={'subscribe-'+item.value} value={item.value}  defaultChecked={true} onChange={handleUnsubscribedFunctionsCheck} onClick={(e) => e.stopPropagation()} />
										</li>
									))}
								</ul> */}
                  <Carousel responsive={responsive} className="subscription">
                    {unsubscribedFunctionList.map((functionName, index) => {
                      const iconMatch = selectFunctions.find(
                        (sf) => sf.value === functionName
                      );
                      return (
                          <div key={index} className="label-wrap">
                            <label htmlFor={"subscribe-" + functionName}>
                              <input
                                name="functions-unsubscribed"
                                type="radio"
                                id={"subscribe-" + functionName}
                                value={functionName}
                                onChange={handleUnsubscribedFunctionsCheck}
                                /* onClick={(e) => e.stopPropagation()} */ defaultChecked={
                                  index === 0
                                }
                              />
                              <span className="image">
                                {iconMatch?.attribute1 && (
                                  <img
                                    src={iconMatch.attribute1}
                                    alt={functionName}
                                    width="50"
                                  />
                                )}
                              </span>
                              <span className="text">{functionName}</span>
                            </label>
                            {/* <span className="image"><img src={item.attribute1 && item.attribute1} alt={item.value} width="38" /></span>
											<Form.Check inline label={item.value} name="functions" type="radio" id={'subscribe-'+item.value} value={item.value}  defaultChecked={true} onChange={handleUnsubscribedFunctionsCheck} onClick={(e) => e.stopPropagation()} /> */}
                            {/* <button className='subscribe' title='Subscribe' onClick={(e) => handleRequest(item.value, e)}>Subscribe</button> */}
                          </div>
                      );
                    })}
                  </Carousel>
                </section>
              )}
            </Container>
          </main>


        </div>
      </div>
    </>
  );
};

export default Subscription;
